// eslint-disable-next-line import/no-unresolved
import Keycloak from 'keycloak-js';
import { ensurePromiseWithResolvers } from '../../../config/promiseWithResolversPolyfill';
import { normalizeKeycloakConfig } from './authFlow';

/**
 * Keycloak Adapter for keycloak-js v26
 * Provides a consistent interface and handles version-specific differences
 */
class KeycloakAdapter {
  constructor(config) {
    if (!config) {
      throw new Error('Keycloak config is required');
    }

    const normalized = normalizeKeycloakConfig(config);
    if (!normalized) {
      throw new Error('Keycloak config must include url, realm, and clientId');
    }

    this.config = normalized;
    this.keycloak = null;
    this.isInitialized = false;
  }

  /**
   * Initialize Keycloak instance
   * @param {Object} initOptions - Initialization options
   * @param {string} initOptions.onLoad - 'login-required' | 'check-sso'
   * @param {string} initOptions.flow - 'standard' | 'hybrid' | 'implicit'
   * @param {boolean} initOptions.checkLoginIframe - Enable/disable iframe check
   * @param {number} initOptions.checkLoginIframeInterval - Interval for iframe check
   * @returns {Promise<boolean>} - Returns true if authenticated
   */
  async init(initOptions = {}) {
    try {
      // Create Keycloak instance
      // keycloak-js v26: Default export is the Keycloak class
      const KeycloakClass = Keycloak.default || Keycloak;

      if (!KeycloakClass || typeof KeycloakClass !== 'function') {
        throw new Error('Keycloak class not found. Make sure keycloak-js is properly imported.');
      }

      try {
        this.keycloak = new KeycloakClass(this.config);
      } catch (constructorError) {
        console.error('Keycloak constructor error:', constructorError);
        const errorMsg = (constructorError && constructorError.message) || 'Unknown error';
        throw new Error(`Failed to create Keycloak instance: ${errorMsg}`);
      }

      if (!this.keycloak) {
        throw new Error('Keycloak instance is null or undefined after creation');
      }

      // Set up error event listeners for Keycloak v26
      // These can catch errors that might not be thrown as promise rejections
      this.keycloak.onAuthError = (error) => {
        console.error('Keycloak auth error event:', error);
      };

      // Default options for v26
      const defaultOptions = {
        onLoad: 'login-required',
        flow: 'standard',
        // v26: checkLoginIframe disabled by default to avoid timeout errors
        // This is safe when using 'login-required' as user will be redirected anyway
        checkLoginIframe: false,
        checkLoginIframeInterval: 5,
        // v26: promiseType removed (native promises are now default)
      };

      const options = { ...defaultOptions, ...initOptions };

      // Initialize Keycloak
      // Keycloak v26 init() returns a Promise<boolean>
      let authenticated;
      try {
        authenticated = await this.keycloak.init(options);
      } catch (initError) {
        console.error('Keycloak.init() error:', initError);
        throw initError !== undefined && initError !== null
          ? initError
          : new Error('Keycloak init() failed. This may indicate a network issue, CORS problem, or invalid configuration.');
      }

      this.isInitialized = true;

      return authenticated === true;
    } catch (error) {
      console.error('Keycloak initialization error:', error);
      this.isInitialized = false;
      throw this._normalizeError(error); // eslint-disable-line no-underscore-dangle
    }
  }

  /**
   * Get authentication status
   * @returns {boolean}
   */
  isAuthenticated() {
    return this.keycloak && this.keycloak.authenticated === true;
  }

  /**
   * Get access token
   * @returns {string|null}
   */
  getToken() {
    return this.keycloak.token ? this.keycloak.token : null;
  }

  /**
   * Get ID token
   * @returns {string|null}
   */
  getIdToken() {
    return this.keycloak.idToken ? this.keycloak.idToken : null;
  }

  /**
   * Get refresh token
   * @returns {string|null}
   */
  getRefreshToken() {
    return this.keycloak.refreshToken ? this.keycloak.refreshToken : null;
  }

  /**
   * Update token if needed
   * @param {number} minValidity - Minimum validity in seconds
   * @returns {Promise<boolean>} - Returns true if token was refreshed
   */
  async updateToken(minValidity = 30) {
    if (!this.keycloak) {
      throw new Error('Keycloak not initialized');
    }

    try {
      ensurePromiseWithResolvers();
      return await this.keycloak.updateToken(minValidity);
    } catch (error) {
      console.error('Token update error:', error);
      throw this._normalizeError(error); // eslint-disable-line no-underscore-dangle
    }
  }

  /**
   * Login
   * @param {Object} options - Login options
   * @returns {Promise<void>}
   */
  async login(options = {}) {
    if (!this.keycloak) {
      throw new Error('Keycloak not initialized');
    }

    try {
      await this.keycloak.login(options);
    } catch (error) {
      console.error('Login error:', error);
      throw this._normalizeError(error); // eslint-disable-line no-underscore-dangle
    }
  }

  /**
   * Logout
   * @param {Object} options - Logout options
   * @returns {Promise<void>}
   */
  async logout(options = {}) {
    if (!this.keycloak) {
      throw new Error('Keycloak not initialized');
    }

    try {
      // v26: Use post_logout_redirect_uri instead of redirectUri
      const logoutOptions = { ...options };
      if (options.redirectUri && !options.post_logout_redirect_uri) {
        logoutOptions.post_logout_redirect_uri = options.redirectUri;
        delete logoutOptions.redirectUri;
      }

      await this.keycloak.logout(logoutOptions);
    } catch (error) {
      console.error('Logout error:', error);
      throw this._normalizeError(error); // eslint-disable-line no-underscore-dangle
    }
  }

  /**
   * Register
   * @param {Object} options - Registration options
   * @returns {Promise<void>}
   */
  async register(options = {}) {
    if (!this.keycloak) {
      throw new Error('Keycloak not initialized');
    }

    try {
      await this.keycloak.register(options);
    } catch (error) {
      console.error('Register error:', error);
      throw this._normalizeError(error); // eslint-disable-line no-underscore-dangle
    }
  }

  /**
   * Get the underlying Keycloak instance
   * @returns {Object|null}
   */
  getInstance() {
    return this.keycloak;
  }

  /**
   * Check if token is expired
   * @returns {boolean}
   */
  isTokenExpired() {
    if (!this.keycloak) {
      return true;
    }

    // v26: Token expiration is handled internally, but we can check if token exists
    return !this.keycloak.token || this.keycloak.isTokenExpired();
  }

  /**
   * Normalize errors from Keycloak
   * @private
   */
  /* eslint-disable-next-line class-methods-use-this */
  _normalizeError(error) {
    // Handle undefined/null errors
    if (error === undefined || error === null) {
      return new Error('Keycloak initialization failed: Unknown error');
    }

    // If it's already an Error, return it
    if (error instanceof Error) {
      return error;
    }

    // Handle error objects - extract message from common properties
    if (typeof error === 'object') {
      const message = error.error || error.message || error.errorMessage || error.description;
      if (message) {
        return new Error(typeof message === 'string' ? message : String(message));
      }
      // If no message found, stringify the error object
      return new Error(`Keycloak error: ${JSON.stringify(error)}`);
    }

    // Handle string errors
    if (typeof error === 'string') {
      return new Error(error);
    }

    // Convert to string as last resort
    return new Error(String(error));
  }

  /**
   * Set event handlers
   * @param {Object} handlers - Event handlers
   * @param {Function} handlers.onAuthSuccess - Called on successful authentication
   * @param {Function} handlers.onAuthError - Called on authentication error
   * @param {Function} handlers.onTokenExpired - Called when token expires
   */
  setEventHandlers(handlers = {}) {
    if (!this.keycloak) {
      throw new Error('Keycloak not initialized');
    }

    if (handlers.onAuthSuccess) {
      this.keycloak.onAuthSuccess = handlers.onAuthSuccess;
    }

    if (handlers.onAuthError) {
      this.keycloak.onAuthError = handlers.onAuthError;
    }

    if (handlers.onTokenExpired) {
      this.keycloak.onTokenExpired = handlers.onTokenExpired;
    }

    if (handlers.onActionUpdate) {
      this.keycloak.onActionUpdate = handlers.onActionUpdate;
    }
  }
}

/**
 * Factory function to create a Keycloak adapter instance
 * @param {Object} config - Keycloak configuration
 * @returns {KeycloakAdapter}
 */
export function createKeycloakAdapter(config) {
  return new KeycloakAdapter(config);
}

export default KeycloakAdapter;

